import { gossips } from '@/mocks/articlesGossip';
import { news } from '@/mocks/articlesNews';
import { stars } from '@/mocks/stars';
import { categories } from '@/mocks/categories';

export interface Article {
  id: string;
  title: string;
  type: string;
  category: string;
  categoryName: string;
  newsTag?: string;
  tags: string[];
  cover: string;
  gallery: string[];
  excerpt: string;
  content: string[];
  author: string;
  source: string;
  publishTime: string;
  views: number;
  likes: number;
  isHot: boolean;
  starId?: string;
}

export interface Star {
  id: string;
  name: string;
  avatar: string;
  title: string;
  bio: string;
  tags: string[];
  followers: string;
  heat: number;
}

export interface Category {
  key: string;
  name: string;
  desc: string;
  icon: string;
}

export const allArticles: Article[] = [...gossips, ...news];

export const allStars: Star[] = stars;
export const allCategories: Category[] = categories;

export function getGossips(): Article[] {
  return allArticles.filter((a) => a.type === 'gossip');
}

export function getNews(): Article[] {
  return allArticles.filter((a) => a.type === 'news');
}

export function getArticleById(id: string): Article | undefined {
  return allArticles.find((a) => a.id === id);
}

export function getStarById(id: string): Star | undefined {
  return allStars.find((s) => s.id === id);
}

export function getCategoryByKey(key: string): Category | undefined {
  return allCategories.find((c) => c.key === key);
}

export function getArticlesByStar(starId: string): Article[] {
  return allArticles
    .filter((a) => a.starId === starId)
    .sort((a, b) => +new Date(b.publishTime) - +new Date(a.publishTime));
}

export function getHotGossips(limit = 5): Article[] {
  return getGossips()
    .slice()
    .sort((a, b) => b.views - a.views)
    .slice(0, limit);
}

export function getLatestArticles(limit = 8): Article[] {
  return allArticles
    .slice()
    .sort((a, b) => +new Date(b.publishTime) - +new Date(a.publishTime))
    .slice(0, limit);
}

export type SortKey = 'latest' | 'hot';

export function sortArticles(list: Article[], sort: SortKey): Article[] {
  const copy = list.slice();
  if (sort === 'hot') {
    return copy.sort((a, b) => b.views - a.views);
  }
  return copy.sort((a, b) => +new Date(b.publishTime) - +new Date(a.publishTime));
}

export interface FilterOptions {
  type?: string;
  category?: string;
  newsTag?: string;
  tags?: string[];
  keyword?: string;
  sort?: SortKey;
}

export function filterArticles(options: FilterOptions): Article[] {
  const { type, category, newsTag, tags, keyword, sort = 'latest' } = options;
  let list = allArticles.slice();

  if (type) list = list.filter((a) => a.type === type);
  if (category) list = list.filter((a) => a.category === category);
  if (newsTag) list = list.filter((a) => a.newsTag === newsTag);
  if (tags && tags.length > 0) {
    list = list.filter((a) => tags.every((t) => a.tags.includes(t)));
  }
  if (keyword) {
    const kw = keyword.trim().toLowerCase();
    if (kw) {
      list = list.filter(
        (a) =>
          a.title.toLowerCase().includes(kw) ||
          a.excerpt.toLowerCase().includes(kw) ||
          a.content.join('').toLowerCase().includes(kw) ||
          a.tags.join('').toLowerCase().includes(kw),
      );
    }
  }
  return sortArticles(list, sort);
}

export function searchArticles(keyword: string, type?: string): Article[] {
  return filterArticles({ keyword, type, sort: 'latest' });
}

export function getRelatedArticles(article: Article, limit = 4): Article[] {
  const scored = allArticles
    .filter((a) => a.id !== article.id)
    .map((a) => {
      let score = 0;
      if (a.category === article.category) score += 3;
      if (a.starId && a.starId === article.starId) score += 4;
      score += a.tags.filter((t) => article.tags.includes(t)).length * 2;
      return { article: a, score };
    })
    .filter((item) => item.score > 0)
    .sort((a, b) => b.score - a.score);
  return scored.slice(0, limit).map((item) => item.article);
}

export function getHotKeywords(): string[] {
  return ['顶流密会', '片场风波', '综艺剪辑', '豪门婚变', '假唱', '退团', '颁奖礼', '翻拍争议'];
}